import React from "react";

import CheckIcon from "@uui/Check";
import { filter } from "lodash/fp";
import { useNavigate } from "react-router-dom";

import { Stack, Typography } from "@mui/material";

import ProviderButton from "@app/analytics/components/ProviderButton";
import useIntegration from "@app/analytics/hooks/integrations/useIntegration";
import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import {
  PRODUCTIVITY_PROVIDERS,
  PRODUCTIVITY_PROVIDERS_BY_ID,
} from "@app/common/constants/providers";
import type {
  ProductivityProvider,
  ProductivityProviderId,
} from "@app/common/types/providerTypes";
import { withQueryParamsUrl } from "@app/common/utils";

import IconBackground from "@rui/IconBackground";

const IntegrationConnectUpsell = () => {
  const navigate = useNavigate();

  const { getIntegration, getEnableUrl } = useIntegrations();
  const { enable, authorize } = useIntegration();

  const handleAuthorize = (provider: ProductivityProvider) => {
    authorize.mutate({
      providerId: provider.id,
      returnRoute: provider.integrationUrl,
      scope: "user",
    });
  };

  const handleConnect = (providerId: ProductivityProviderId) => {
    const enableUrl = getEnableUrl({ providerId });
    const providerConfig = PRODUCTIVITY_PROVIDERS_BY_ID[providerId];

    // if we can enable the integration without pushing the user through
    // another auth flow, try to do that
    if (enableUrl) {
      enable.mutate(enableUrl, {
        onSuccess: () => {
          navigate(
            withQueryParamsUrl(providerConfig.integrationUrl, {
              action: providerConfig.connectAction,
              success: true,
            })
          );
        },
        onError: () => handleAuthorize(providerConfig),
      });
    } else {
      handleAuthorize(providerConfig);
    }
  };

  const connectableIntegrations = filter(({ id }) => {
    const userIntegration = getIntegration({ providerId: id, scope: "user" });
    return (
      userIntegration != null &&
      !userIntegration.isDisabled &&
      !userIntegration.isConnected
    );
  }, PRODUCTIVITY_PROVIDERS);

  return (
    <>
      <Typography
        variant="bodyPrimaryReg"
        fontWeight={600}
        color="shades.neutral.800"
      >
        Calendar integrations have been disabled for your Workspace
      </Typography>
      {connectableIntegrations.length > 0 && (
        <>
          <Typography
            variant="bodySecondaryReg"
            color="shades.neutral.700"
            lineHeight={1.5}
          >
            Get started with Productivity AI by connecting an email or messaging
            integration below.
          </Typography>
          <Stack spacing="6px" width="100%">
            {connectableIntegrations.map((provider) => {
              const isConnected = getIntegration({
                providerId: provider.id,
                scope: "user",
              })?.isConnected;

              return (
                <ProviderButton
                  key={provider.id}
                  icon={
                    <img
                      src={provider.icon}
                      alt={`Connect with ${provider.displayName}`}
                    />
                  }
                  providerId={provider.id}
                  onClick={() => handleConnect(provider.id)}
                  endIcon={
                    isConnected ? (
                      <IconBackground height={24} bgcolor="shades.meadow.50">
                        <CheckIcon sx={{ color: "shades.meadow.500" }} />
                      </IconBackground>
                    ) : undefined
                  }
                >
                  Connect with {provider.displayNameShort}
                </ProviderButton>
              );
            })}
          </Stack>
        </>
      )}
    </>
  );
};

export default IntegrationConnectUpsell;
